import React, { useEffect, useState } from "react";
import { ShoppingCart, Loader2, Crown, Plus, Minus } from "lucide-react";
import { api, money } from "@/lib/api";
import { useMerchCart } from "@/context/MerchCartContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const COLLECTIONS = ["All", "T-Shirts", "Hoodies & Sweatshirts", "Hats", "Tote Bags", "Drinkware", "Accessories", "Limited Edition"];

export default function MerchPage() {
  const { add, count, setOpen } = useMerchCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [col, setCol] = useState("All");
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/merch/products").then((r) => setProducts(r.data)).catch(() => toast.error("Could not load merch. Please refresh.")).finally(() => setLoading(false)); }, []);

  const shown = col === "All" ? products : products.filter((p) => p.collection === col);

  const addToCart = (p, size, color, qty) => {
    if (p.sizes?.length && !size) { toast.error("Please select a size before adding this item to your cart."); return; }
    if (p.colors?.length && !color) { toast.error("Please select a color before adding this item to your cart."); return; }
    if ((p.unavailable_variants || []).includes(`${size || "One Size"}|${color || "Default"}`)) { toast.error("This product variant is currently unavailable."); return; }
    add(p, size, color, qty);
    setDetail(null);
  };

  return (
    <div className="min-h-screen">
      <section className="relative py-16 tropical-grain border-b border-emerald-900/50 text-center">
        <div className="max-w-4xl mx-auto px-5">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400 bg-emerald-950/60 border border-amber-500/30 rounded-full px-4 py-2"><Crown className="w-3.5 h-3.5" /> Official Merch</span>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5">Sir. King Kitchen <span className="text-gradient-gold">Merch</span></h1>
          <p className="text-lg text-[#FDFBF7]/85 mt-3 font-display uppercase tracking-wide">Wear the Flavor. Represent the King.</p>
          <p className="text-sm text-[#FDFBF7]/55 mt-2">Take a little Sir. King Kitchen with you wherever you go.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-4">
          {COLLECTIONS.map((c) => (
            <button key={c} data-testid={`merch-collection-${c.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setCol(c)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-display font-bold uppercase tracking-wide border transition-colors ${col === c ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>{c}</button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-20 text-amber-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-16 text-[#FDFBF7]/60">No products in this collection yet.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {shown.map((p) => (
              <div key={p.product_id} data-testid={`merch-product-${p.product_id}`} className="group rounded-2xl bg-[#181D1A] border border-emerald-900/60 overflow-hidden hover:border-amber-500/50 transition-colors">
                <button onClick={() => setDetail(p)} className="block w-full aspect-square overflow-hidden bg-[#101311]">
                  <img src={p.images?.[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </button>
                <div className="p-4">
                  {p.limited_edition && <span className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Limited Edition</span>}
                  <h3 className="font-display font-bold leading-tight mt-0.5">{p.name}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-amber-400 font-black">{money(p.retail_price)}</span>
                    <button data-testid={`merch-view-${p.product_id}`} onClick={() => setDetail(p)} className="text-xs px-3 py-1.5 rounded-full bg-amber-400 text-[#0A2818] font-bold uppercase">View</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {count > 0 && (
        <button data-testid="merch-cart-fab" onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-6 py-4 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase shadow-xl shadow-amber-500/30 active:scale-95 transition-transform">
          <ShoppingCart className="w-5 h-5" /> View Cart · {count}
        </button>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <div className="rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=https://sirkingmaine.com/merch" alt="Scan to shop merch" className="w-20 h-20 rounded-lg bg-white p-1" />
            <div>
              <div className="font-display font-black uppercase">Scan to Shop the Merch</div>
              <p className="text-sm text-[#FDFBF7]/60">Share this at the restaurant, on bags & flyers.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a data-testid="merch-returns-link" href="/merch/returns" className="px-5 py-2.5 rounded-full border border-amber-500/50 text-amber-300 font-display font-bold uppercase text-sm">Returns & FAQ / Support</a>
            <a data-testid="merch-track-link" href="/track" className="px-5 py-2.5 rounded-full border border-emerald-600/50 text-emerald-300 font-display font-bold uppercase text-sm">Track My Order</a>
          </div>
        </div>
      </section>

      <ProductDialog p={detail} onClose={() => setDetail(null)} onAdd={addToCart} />
    </div>
  );
}

function ProductDialog({ p, onClose, onAdd }) {
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  useEffect(() => { if (p) { setSize(p.sizes?.[0] || null); setColor(p.colors?.[0] || null); setQty(1); } }, [p]);
  if (!p) return null;
  const unavail = p.unavailable_variants || [];
  const soldOut = (s, c) => unavail.includes(`${s || "One Size"}|${c || "Default"}`);
  const comboSoldOut = soldOut(size, color);
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181D1A] border-emerald-800 text-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{p.name}</DialogTitle>
        <DialogDescription className="sr-only">Choose size, color and quantity, then add {p.name} to your cart.</DialogDescription>
        <div className="grid sm:grid-cols-2 gap-5">
          <img src={p.images?.[0]} alt={p.name} className="w-full rounded-xl object-cover bg-[#101311]" />
          <div>
            {p.limited_edition && <span className="text-[11px] uppercase tracking-widest text-red-400 font-bold">Limited Edition · While Supplies Last</span>}
            <h2 className="font-display font-black text-2xl mt-1">{p.name}</h2>
            <div className="text-amber-400 font-black text-2xl mt-1">{money(p.retail_price)}</div>
            <p className="text-sm text-[#FDFBF7]/70 mt-3">{p.description}</p>
            {p.sizes?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wide text-amber-400 font-bold mb-1.5">Size</div>
                <div className="flex flex-wrap gap-2">{p.sizes.map((s) => { const off = soldOut(s, color); return <button key={s} data-testid={`merch-size-${s}`} disabled={off} onClick={() => setSize(s)} className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${off ? "border-emerald-900/40 text-[#FDFBF7]/25 line-through cursor-not-allowed" : size === s ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80"}`}>{s}</button>; })}</div>
              </div>
            )}
            {p.colors?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wide text-amber-400 font-bold mb-1.5">Color</div>
                <div className="flex flex-wrap gap-2">{p.colors.map((c) => { const off = soldOut(size, c); return <button key={c} data-testid={`merch-color-${c.replace(/[^a-zA-Z]/g, "-")}`} disabled={off} onClick={() => setColor(c)} className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${off ? "border-emerald-900/40 text-[#FDFBF7]/25 line-through cursor-not-allowed" : color === c ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80"}`}>{c}</button>; })}</div>
              </div>
            )}
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-2 border border-emerald-700/50 rounded-full px-2 py-1">
                <button data-testid="merch-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1"><Minus className="w-4 h-4" /></button>
                <span className="w-6 text-center font-bold" data-testid="merch-qty">{qty}</span>
                <button data-testid="merch-qty-inc" onClick={() => setQty((q) => q + 1)} className="p-1"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
            <button data-testid="merch-add-to-cart" disabled={comboSoldOut} onClick={() => onAdd(p, size, color, qty)} className="mt-5 w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-emerald-900/40 disabled:text-[#FDFBF7]/40 disabled:cursor-not-allowed text-[#0A2818] font-display font-black uppercase tracking-wide">{comboSoldOut ? "Sold Out" : `Add to Cart · ${money(p.retail_price * qty)}`}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
